import { z } from 'zod';
import { DEFAULT_COMPANY_LOCALE, isValidPostcode, type CompanyLocale } from '@/utils/locale';

/**
 * Lead form schema for the tenant's country (postal-code rule follows the
 * locale). Build it with the locale from `useCompanyLocale()`.
 */
export function createLeadFormBaseSchema(locale: CompanyLocale = DEFAULT_COMPANY_LOCALE) {
  return z.object({
    customer_type: z.enum(['individual', 'company']).default('individual'),
    customer_name: z.string().trim().min(1, 'Customer name is required').max(100),
    company_name: z.string().trim().max(100).optional(),
    customer_phone: z.string().trim().min(1, 'Phone is required').max(20),
    customer_email: z.string().trim().email('Invalid email').max(255).optional().or(z.literal('')),
    customer_address: z.string().trim().max(500).optional().or(z.literal('')),
    customer_suburb: z.string().trim().max(100).optional(),
    customer_state: z.string().optional(),
    customer_postcode: z
      .string()
      .trim()
      .optional()
      .or(z.literal(''))
      .refine((v) => isValidPostcode(v, locale), locale.postcode.message),
    issue_description: z.string().trim().min(1, 'Issue description is required').max(1000),
    service_type_id: z.string().optional(),
    lead_source_id: z.string().optional(),
    campaign_id: z.string().optional(),
    quoted_price: z.string().optional(),
    scheduled_date: z.string().optional(),
    scheduled_time: z.string().optional(),
    notes: z.string().trim().max(2000).optional(),
    assigned_tech_id: z.string().optional(),
    assigned_csr_id: z.string().optional(),
    lead_owner_id: z.string().optional(),
    call_classification: z.enum(['service_lead', 'vendor_call', 'general_inquiry', 'spam_junk', 'existing_customer']).default('service_lead'),
    lat: z.number().nullable().optional(),
    lng: z.number().nullable().optional(),
  });
}

/**
 * Manual lead entry must say where the lead came from, otherwise it never
 * appears in any advertising report (182 of 184 manual leads in the 90 days
 * to 2026-09-08 became jobs and were attributed to nothing). Editing an
 * existing lead keeps the base schema so old records still save.
 */
export function createLeadFormSchema(locale: CompanyLocale = DEFAULT_COMPANY_LOCALE) {
  return createLeadFormBaseSchema(locale).extend({
    lead_source_id: z.string().min(1, 'Select where this lead came from'),
  });
}

/** Australian-default schemas, kept for callers that have no tenant context. */
export const leadFormSchema = createLeadFormBaseSchema();
export const createLeadFormSchemaDefault = createLeadFormSchema();

export type LeadFormData = z.infer<typeof leadFormSchema>;
